import React, { useRef } from 'react';
import Tag from '@frontend/app/refresh-components/Tag';
import { Tooltip, TooltipContent, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';
import { cn } from '@frontend/shadcn/lib/utils';
import { LockIcon, CalendarIcon, ArrowsRotateIcon } from '@revfluence/fresh-icons/regular/esm';
import { ShoppingCartIcon } from 'lucide-react';
import { DollarCircleFilled } from '@ant-design/icons';
import { ConditionTypeMap, PayoutTag, PayoutTagsProps } from './types';

const getTagColor = (type: PayoutTag['type']): { bg: string; text: string } => {
  switch (type) {
    case ConditionTypeMap.COLLECTIONS:
      return { bg: 'bg-blue-4', text: 'text-grey-0' };
    case ConditionTypeMap.PRODUCT:
      return { bg: 'bg-purple-4', text: 'text-grey-0' };
    case ConditionTypeMap.ORDER_SUBTOTAL:
      return { bg: 'bg-orange-4', text: 'text-grey-0' };
    case ConditionTypeMap.DATE_RANGE:
      return { bg: 'bg-green-4', text: 'text-grey-0' };
    case ConditionTypeMap.RECURRING_ORDER:
      return { bg: 'bg-red-4', text: 'text-grey-0' };
    default:
      return { bg: 'bg-grey-3', text: 'text-grey-0' };
  }
};

const getTagIcon = (type: PayoutTag['type']): React.ReactNode => {
  switch (type) {
    case ConditionTypeMap.COLLECTIONS:
      return '';
    case ConditionTypeMap.PRODUCT:
      // TODO: replace all icons with fontawesome solid icons
      return <ShoppingCartIcon className="w-3.5 h-3.5" />;
    case ConditionTypeMap.ORDER_SUBTOTAL:
      return <DollarCircleFilled className="w-3.5 h-3.5" />;
    case ConditionTypeMap.DATE_RANGE:
      return <CalendarIcon className="w-3.5 h-3.5" />;
    case ConditionTypeMap.RECURRING_ORDER:
      return <ArrowsRotateIcon className="w-3.5 h-3.5" />;
    default:
      return <LockIcon className="w-3.5 h-3.5" />;
  }
};

const ProductPopover: React.FC<{ products: PayoutTag[] }> = ({ products }) => (
  <div className="flex flex-col gap-1 p-2 min-w-[200px] max-h-[320px] overflow-y-auto bg-white shadow-lg border border-grey-2 rounded-lg">
    <h3 className="text-grey-4 text-xs leading-5 font-normal px-2">
      Applicable on specific products
    </h3>
    <div className="space-y-2">
      {products.map((product) => (
        <div key={product.id} className="flex items-start gap-2 p-2 hover:bg-grey-1 rounded-md">
          <div className="w-6 h-6 rounded-md flex-shrink-0">
            <img
              src={product.imageUrl || 'https://storage.googleapis.com/aspireiq-widgets/assets/content_image_placeholder.png'}
              alt={product.name || product.id}
              className="w-full h-full object-cover rounded-md"
              onError={(e) => {
                e.currentTarget.src = 'https://storage.googleapis.com/aspireiq-widgets/assets/content_image_placeholder.png';
              }}
            />
          </div>
          <span className="text-grey-6 text-xs leading-5 font-normal">
            {product.name || product.id}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const CollectionsPopover: React.FC<{ collections: PayoutTag[] }> = ({ collections }) => (
  <div className="flex flex-col gap-1 p-2 min-w-[200px] max-h-[320px] overflow-y-auto bg-white shadow-lg border border-grey-2 rounded-lg">
    <h3 className="text-grey-4 text-xs leading-5 font-normal px-2">
      Applicable on specific collections
    </h3>
    <div className="space-y-1">
      {collections.map((collection) => (
        <div key={collection.id} className="flex items-start gap-1 px-2 py-1 hover:bg-grey-1 rounded-md">
          <span className="text-grey-6 text-xs leading-5 font-normal">
            {collection.name || collection.id}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const TagsPopover: React.FC<{ tags: React.ReactNode[] }> = ({ tags }) => (
  <div className="flex flex-col min-w-[200px] max-h-[320px] overflow-y-auto bg-white shadow-lg border border-grey-2 rounded-lg">
    <div className="p-2 flex flex-col gap-2">
      {tags.map((tag, index) => (
        <React.Fragment key={index}>
          <div className="px-1">
            {tag}
          </div>
        </React.Fragment>
      ))}
    </div>
  </div>
);

export const PayoutTags: React.FC<PayoutTagsProps> = ({
  tags,
  className,
  showDivider = true,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const tagsContainerRef = useRef<HTMLDivElement>(null);
  const visibleTagCount = 2;

  const getRecurringOrderText = (tag: PayoutTag): string => {
    if (!tag.recurringLimit) return tag.name;

    if (tag.recurringLimit.isLimitPayoutToFirstOrder) {
      return 'One time';
    }
    if (tag.recurringLimit.isPayoutAppliedToAllRecurringOrders) {
      return 'All recurring orders';
    }
    if (tag.recurringLimit.limitPayoutToMultipleRecurringOrder) {
      return `${tag.recurringLimit.limitPayoutToMultipleRecurringOrder} recurring orders`;
    }
    return tag.name;
  };

  const groupedTags = tags.reduce((acc, tag) => {
    if (!acc[tag.type]) {
      acc[tag.type] = [];
    }
    acc[tag.type].push(tag);
    return acc;
  }, {} as Record<string, PayoutTag[]>);

  const renderProductTag = (products: PayoutTag[], usePopover: boolean) => {
    return renderProductTagContent(products, 'Specific Products', usePopover);
  };

  const renderProductTagContent = (products: PayoutTag[], tagLabel: string, usePopover: boolean) => (
    usePopover ? (
      <Tooltip>
        <TooltipTrigger asChild onClick={(e) => e.preventDefault()} className="cursor-pointer">
          <div className="inline-block">
            <Tag
              bgColor={getTagColor('product').bg}
              textColor={getTagColor('product').text}
              label={tagLabel}
              icon={getTagIcon('product')}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="right"
          className="z-50 bg-white shadow-none rounded-lg p-0"
          sideOffset={5}
        >
          <ProductPopover products={products} />
        </TooltipContent>
      </Tooltip>
  ) : (
    <Tag
      bgColor={getTagColor('product').bg}
      textColor={getTagColor('product').text}
      label={products[0].name}
    />
    )
  );

  const renderCollectionTagContent = (collections: PayoutTag[], usePopover: boolean) => {
    if (!usePopover) {
      // Single collection - show direct tag
      return (
        <Tag
          key={collections[0].id}
          bgColor={getTagColor('collection').bg}
          textColor={getTagColor('collection').text}
          label={collections[0].name}
        />
      );
    }

    // Multiple collections - show popover
    return (
      <Tooltip>
        <TooltipTrigger asChild onClick={(e) => e.preventDefault()} className="cursor-pointer">
          <div className="inline-block">
            <Tag
              bgColor={getTagColor('collection').bg}
              textColor={getTagColor('collection').text}
              label="Specific Collections"
              icon={getTagIcon('collection')}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="right"
          className="z-50 bg-white shadow-none rounded-lg p-0"
          sideOffset={5}
        >
          <CollectionsPopover collections={collections} />
        </TooltipContent>
      </Tooltip>
    );
  };

  const renderTags = () => {
    const allTags: React.ReactNode[] = [];

    // First, handle product tags if they exist
    const productTags = groupedTags.product;
    if (productTags?.length) {
      allTags.push(renderProductTag(productTags, productTags.length > 1));
    }

    const collectionsTags = groupedTags.collection;
    if (collectionsTags?.length) {
      allTags.push(renderCollectionTagContent(collectionsTags, collectionsTags.length > 1));
    }

    // Then add all other tags
    Object.entries(groupedTags).forEach(([type, typeTags]) => {
      if (type !== 'product' && type !== 'collection') {
        typeTags.forEach((tag) => {
          allTags.push(
            <Tag
              key={tag.id}
              bgColor={tag.bgColor || getTagColor(tag.type).bg}
              textColor={tag.textColor || getTagColor(tag.type).text}
              label={tag.type === 'recurring' ? getRecurringOrderText(tag) : tag.name}
              icon={getTagIcon(tag.type)}
            />
          );
        });
      }
    });

    const visibleTags = allTags.slice(0, visibleTagCount);
    const hiddenTags = allTags.slice(visibleTagCount);
    const remainingCount = hiddenTags.length;

    return (
      <div ref={tagsContainerRef} className="flex items-center gap-2">
        {visibleTags}
        {remainingCount > 0 && (
          <Tooltip>
            <TooltipTrigger onClick={(e) => e.preventDefault()} className="cursor-pointer">
              <Tag
                bgColor="bg-lime-4"
                textColor="text-grey-0"
                label={`+${remainingCount}`}
              />
            </TooltipTrigger>
            <TooltipContent
              side="right"
              className="z-50 bg-white shadow-none rounded-lg p-0"
              sideOffset={5}
            >
              <TagsPopover tags={hiddenTags} />
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    );
  };

  return (
    <div ref={containerRef} className={cn('flex items-center gap-2 w-full', className)}>
      {showDivider && (
        <span className="text-sm text-grey-5 mr-1 whitespace-nowrap">On:</span>
      )}
      {renderTags()}
    </div>
  );
};

export default PayoutTags;
